/**
 * The icons on the mobile tab bar.
 *
 * The canvas draws them as 18×18 boxes with a 1.5px border — placeholders, not artwork — so the
 * shapes are ours. Rules they follow, so a row of five reads as one set:
 *
 *   - 18×18 on a 24-unit grid, 1.5 stroke, round caps and joins, no fill.
 *   - `currentColor`, so the active tab's blue and the inactive muted grey come from the CSS
 *     that already colours the label underneath. Nothing here knows about the palette.
 *   - One idea per icon, drawn from what the section IS rather than a generic symbol: a house,
 *     a bookmark for the players you keep, the draft board's own grid, the rulebook, and the
 *     three dots that mean "the rest".
 *
 * Inline SVG rather than an icon font or a sprite: five paths cost less than either, and the
 * standalone build has no second file to fetch.
 */

const BOX = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

export type NavIconName = 'home' | 'keepers' | 'draft' | 'league' | 'history' | 'more';

export function NavIcon({ name }: { name: NavIconName }) {
  switch (name) {
    case 'home':
      return (
        <svg {...BOX}>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5.5 9.5V20a1 1 0 0 0 1 1H10v-6h4v6h3.5a1 1 0 0 0 1-1V9.5" />
        </svg>
      );
    /* A bookmark: the players you hold on to. */
    case 'keepers':
      return (
        <svg {...BOX}>
          <path d="M6.5 3h11a1 1 0 0 1 1 1v17l-6.5-4.5L5.5 21V4a1 1 0 0 1 1-1Z" />
        </svg>
      );
    /* The board itself — rounds down, teams across. */
    case 'draft':
      return (
        <svg {...BOX}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M3 15h18M9 3v18" />
        </svg>
      );
    /* The rulebook, which is what League is really about. */
    case 'league':
      return (
        <svg {...BOX}>
          <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19v15H5.5A1.5 1.5 0 0 0 4 19.5Z" />
          <path d="M4 19.5A1.5 1.5 0 0 1 5.5 21H19v-3" />
          <path d="M8 7.5h7M8 11h5" />
        </svg>
      );
    /*
     * A trophy, not a clock. History here is not "things that happened" — it is champions,
     * records and careers, and the section's own first page is the payouts board. A clock face
     * would have promised an audit log.
     */
    case 'history':
      return (
        <svg {...BOX}>
          <path d="M7 4h10v5a5 5 0 0 1-10 0Z" />
          <path d="M7 5.5H4.5V7a3 3 0 0 0 3 3M17 5.5h2.5V7a3 3 0 0 1-3 3" />
          <path d="M12 14v3M9 20h6M10.5 17h3l.5 3h-4Z" />
        </svg>
      );
    case 'more':
    default:
      return (
        <svg {...BOX}>
          <circle cx="5" cy="12" r="1.4" />
          <circle cx="12" cy="12" r="1.4" />
          <circle cx="19" cy="12" r="1.4" />
        </svg>
      );
  }
}
